import * as yup from "yup";
import PersonIcon from "@mui/icons-material/Person";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ReportIcon from "@mui/icons-material/Assessment";
import DatabaseIcon from "@mui/icons-material/StorageSharp";
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  // meta fields
  isActive: boolean;
  isDefault: boolean;
  createdBy: string;
  updatedBy: string;
  dateCreated: string; //change to Date
  dateUpdated: string;
}

export const roleSchema = yup.object({
  name: yup.string().required("Role name is required").trim(),
  description: yup.string().required("Role description is required").trim(),
});

export type RoleFields = Omit<
  Role,
  | "id"
  | "isActive"
  | "createdBy"
  | "updatedBy"
  | "dateCreated"
  | "dateUpdated"
  | "isDefault"
>;

export const roleInitialValues: RoleFields = {
  name: "",
  description: "",
  permissions: ["EJO::VIEW"],
};

export const Permissions = {
  view: "VIEW",
  add: "ADD",
  edit: "EDIT",
  delete: "DELETE",
  export: "EXPORT",
  import: "IMPORT",
  email: "EMAIL",
};

export const PermissionTypes = Object.values(Permissions);

export const Modules = {
  user: {
    accessKey: "USER",
    label: "User",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![Permissions.import, Permissions.email].includes(permission)
    ),
  },
  role: {
    accessKey: "ROLE",
    label: "Role",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![Permissions.export, Permissions.import, Permissions.email].includes(
          permission
        )
    ),
  },
  eur: {
    accessKey: "EUR",
    label: "EUR",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![Permissions.delete, Permissions.export, Permissions.email].includes(permission)
    ),
  },
  fris: {
    accessKey: "FRIS",
    label: "FRIS",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![Permissions.delete, Permissions.export, Permissions.import, Permissions.email].includes(permission)
    ),
  },
  ec: {
    accessKey: "EC",
    label: "Equipment Certificate",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.delete,
          Permissions.export,
          Permissions.import,
        ].includes(permission)
    ),
  },
  ol: {
    accessKey: "OL",
    label: "Operator Licenses",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.delete,
          Permissions.export,
          Permissions.import,
        ].includes(permission)
    ),
  },
  repDaui: {
    accessKey: "REP::DAUI",
    label: "DAUI",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.edit,
          Permissions.delete,
          Permissions.import,
          Permissions.email,
        ].includes(permission)
    ),
  },
  repHeur: {
    accessKey: "REP::HEUR",
    label: "Historical EUR",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.edit,
          Permissions.delete,
          Permissions.import,
          Permissions.email,
        ].includes(permission)
    ),
  },
  repHrent: {
    accessKey: "REP::HRENT",
    label: "Historical Rental",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.edit,
          Permissions.delete,
          Permissions.import,
          Permissions.email,
        ].includes(permission)
    ),
  },
  repCpc: {
    accessKey: "REP::CPC",
    label: "CPC",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.edit,
          Permissions.delete,
          Permissions.import,
          Permissions.email,
        ].includes(permission)
    ),
  },
  repClc: {
    accessKey: "REP::CLC",
    label: "CLC",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.edit,
          Permissions.delete,
          Permissions.import,
          Permissions.email,
        ].includes(permission)
    ),
  },
  repFc: {
    accessKey: "REP::FC",
    label: "Fuel Consumption",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.edit,
          Permissions.delete,
          Permissions.import,
          Permissions.email,
        ].includes(permission)
    ),
  },
  repEst: {
    accessKey: "REP::EST",
    label: "MRRF JO Timeline",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.edit,
          Permissions.delete,
          Permissions.import,
          Permissions.email,
        ].includes(permission)
    ),
  },
  repEm: {
    accessKey: "REP::EM",
    label: "MRRF Manpower",
    permissions: PermissionTypes.filter(
      (permission) =>
        ![
          Permissions.add,
          Permissions.edit,
          Permissions.delete,
          Permissions.import,
          Permissions.email,
        ].includes(permission)
    ),
  },
  mmdEq: {
    accessKey: "MMD::EQ",
    label: "Equipment",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.delete, Permissions.email].includes(permission)
    ),
  },
  mmdEc: {
    accessKey: "MMD::EC",
    label: "Equipment Certificate",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdPms: {
    accessKey: "MMD::PMS",
    label: "PMS Code",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdPct: {
    accessKey: "MMD::PCT",
    label: "PMS Code + Task",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdPd: {
    accessKey: "MMD::PD",
    label: "Parts Description",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdParts: {
    accessKey: "MMD::PARTS",
    label: "Parts",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.delete, Permissions.email].includes(permission)
    ),
  },
  mmdSrt: {
    accessKey: "MMD::SRT",
    label: "Service Request Type",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdTt: {
    accessKey: "MMD::TT",
    label: "Task Type",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdLt: {
    accessKey: "MMD::LT",
    label: "Labor Type + Rate",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdMech: {
    accessKey: "MMD::MECH",
    label: "Mechanic",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdOp: {
    accessKey: "MMD::OP",
    label: "Operator",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdOc: {
    accessKey: "MMD::OC",
    label: "Operator Certificate",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdFt: {
    accessKey: "MMD::FT",
    label: "Fuel Type",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  mmdLoc: {
    accessKey: "MMD::LOC",
    label: "Location",
    permissions: PermissionTypes.filter(
      (permission) => ![Permissions.email].includes(permission)
    ),
  },
  ejoManagement: { accessKey: "EJO::MANAGEMENT", label: "MRRF Management" },
  ejoEsrApproval: { accessKey: "EJO::ESR::APPROVAL", label: "Approval" },
  esrManagement: { accessKey: "ESR::MANAGEMENT", label: "JO Management" },
  reqManagement: { accessKey: "REQ::MANAGEMENT", label: "Request Management" },
  allocManagement: {
    accessKey: "ALLOC::MANAGEMENT",
    label: "Allocation Management",
  },
};

export const MODULES = [
  {
    modules: [Modules.user, Modules.role],
    title: "User Management",
    icon: <PersonIcon />,
  },
  {
    modules: [Modules.eur, Modules.fris, Modules.ec, Modules.ol],
    title: "View My Dashboard + Task",
    icon: <DashboardIcon />,
  },
  {
    modules: [
      Modules.repDaui,
      Modules.repHeur,
      Modules.repHrent,
      Modules.repCpc,
      Modules.repClc,
      Modules.repFc,
      Modules.repEst,
      Modules.repEm,
    ],
    title: "Reports",
    icon: <ReportIcon />,
  },
  {
    modules: [
      Modules.mmdEq,
      Modules.mmdEc,
      Modules.mmdPms,
      Modules.mmdPct,
      Modules.mmdPd,
      Modules.mmdParts,
      Modules.mmdSrt,
      Modules.mmdTt,
      Modules.mmdLt,
      Modules.mmdMech,
      Modules.mmdOp,
      Modules.mmdOc,
      Modules.mmdFt,
      Modules.mmdLoc,
    ],
    title: "Manage My Databases",
    icon: <DatabaseIcon />,
  },
]; // add more

export const EquipmentRepairProcessOptions: Array<{
  label: string;
  accessKey: string;
}> = [
  Modules.ejoManagement,
  Modules.ejoEsrApproval,
  Modules.esrManagement,
];

export const EquipmentRepairProcessModule = {
  title: "Equipment Repair Process",
  permissions: EquipmentRepairProcessOptions,
  icon: <HomeRepairServiceIcon />,
};

export const EquipmentRentalProcessOptions: Array<{
  label: string;
  accessKey: string;
}> = [Modules.reqManagement, Modules.allocManagement];

export const EquipmentRentalProcessModule = {
  title: "Equipment Rental Process",
  permissions: EquipmentRentalProcessOptions,
  icon: <NoteAltIcon />,
};
